import { useMemo, useCallback, useEffect, useRef, memo } from 'react';
import Flatpickr from 'react-flatpickr';
import { useTranslation } from 'react-i18next';
import { MandarinTraditional } from 'flatpickr/dist/l10n/zh-tw.js';
import { Japanese } from 'flatpickr/dist/l10n/ja.js';

const LOCALES = {
  zh: MandarinTraditional,
  ja: Japanese,
};

function shallowEqual(a, b) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

// react-flatpickr（目前裝的 4.0.11 已是 npm 最新版）內部用 useMemo(() => ({...ownProps}), [ownProps])
// 記憶設定，但 ownProps 是 JSX 每次求值就會產生的新物件——即使個別欄位參照都沒變，也會被判定
// 成「變了」，導致底下的 flatpickr 實例整個被銷毀重建。若把 value 當一般 controlled prop 直接
// 傳給 <Flatpickr>，使用者互動選值時就會：選值 → onChange → 外層 state 更新 → value prop 變化
// → 觸發重建，畫面上看起來像日曆整個閃一下重繪。
// 拆成內層 FlatpickrInstance：完全不接收 value，用 React.memo 擋掉「只有 value 變了」這種
// re-render，讓 <Flatpickr> 真正維持同一個實例；value 的同步改由外層 DatePicker 的 useEffect
// 呼叫 flatpickr 實例既有的 setDate()（命令式、不經過 <Flatpickr> 的 props 系統，不會觸發重建）。
// 注意：拿實例不能用 ref——react-flatpickr 4.0.11 是普通 function component、沒有用
// forwardRef 包裝（它是照 React 19 的 ref-as-prop 寫的，read props.ref），在本專案的 React 18
// 下 <Flatpickr ref={...}> 永遠拿不到東西，只能靠它明確支援的 onCreate callback prop 取得實例。
const FlatpickrInstance = memo(
  function FlatpickrInstance({ options, onChange, onCreate, inputProps, className }) {
    return (
      <Flatpickr
        options={options}
        onChange={onChange}
        // onClose 不是冗餘的：使用者手打日期後直接點別處時，flatpickr 的 documentClick 會先用
        // triggerChange=false 靜默 setDate 並改寫 input.value（index.js:1017-1022），等 onBlur
        // 跑到時 valueChanged 已經是 false（index.js:1016-1025）→ onChange 根本不會觸發，
        // 只剩 close() 的 onClose（index.js:831）能把值同步回 React state
        onClose={onChange}
        onCreate={onCreate}
        render={({ defaultValue }, inputRef) => (
          <input
            {...inputProps}
            ref={inputRef}
            className={`form-control ${className}`.trim()}
            defaultValue={defaultValue}
          />
        )}
      />
    );
  },
  (prev, next) =>
    prev.options === next.options &&
    prev.onChange === next.onChange &&
    prev.onCreate === next.onCreate &&
    prev.className === next.className &&
    shallowEqual(prev.inputProps, next.inputProps)
);
FlatpickrInstance.displayName = 'FlatpickrInstance';

// 全站共用日期/日期時間選擇器，統一樣式（見 custom.scss 的 flatpickr 主色覆寫）
//
// ⚠️ 放進 modal 使用時，該 <Modal> 必須加 enforceFocus={false}
// flatpickr 把日曆 appendChild 到 document.body（index.js:413-416），所以日曆永遠長在 modal 之外。
// react-bootstrap 的 Modal 預設監聽 document 的 focus，焦點只要落到 dialog 外就搶回 dialog
// （@restart/ui Modal.js:112-115 註冊、165-172 執行），於是日曆內所有需要焦點的元素——時、分、年
// 的輸入框與月份 select——都會在取得焦點的下一個 tick 被奪走，表現為「點下去游標閃一下就沒了、
// 打不進字、select 下拉打不開」，只有純滑鼠點擊的日期格與上下箭頭還能用。
// 註：曾試過改用 flatpickr 的 static: true 把日曆包進 modal 內，但 bs-custom.scss:141 的
// .modal .modal-content{overflow-y:auto} 會讓 modal-content 成為 scroll container，把絕對定位的
// 日曆裁掉，而時間列正好在日曆最下方，反而更糟；維持浮層還能在空間不足時自動往上翻。
//
// allowInput 必須是 true：flatpickr 在 allowInput: false 時會對底層 input 加上 readonly，
// readonly 欄位會被排除在 constraint validation 之外，required 會完全失效
// 必須用 render prop 自行渲染 input：react-flatpickr 在預設（非 render）模式下，
// 會另外把自己的 onChange 直接綁在 React input 的 onChange 上，用 new Date(input.value)
// 這種不管 dateFormat/locale 的土法解析，且每個按鍵都會觸發、與 controlled value 互相打架，
// 用 render 繞開這條路徑，只依賴 flatpickr 自己（正確、只在選定/失焦時觸發）的 hook 系統
function DatePicker({ value, onChange, enableTime = false, minDate, maxDate, defaultHour, defaultMinute, className = '', ...inputProps }) {
  const { i18n } = useTranslation();
  const locale = LOCALES[i18n.language];
  const instanceRef = useRef(null);
  // 只在掛載當下取一次，當 flatpickr 的初始顯示值；掛載後的 value 變化一律交給下面的
  // useEffect 用 setDate() 命令式同步，不再進到 options 這個依賴陣列裡
  const initialValueRef = useRef(value);

  const options = useMemo(() => ({
    ...(locale ? { locale } : {}),
    enableTime,
    time_24hr: true,
    minuteIncrement: 1,
    dateFormat: enableTime ? 'Y-m-d H:i' : 'Y-m-d',
    allowInput: true,
    minDate,
    maxDate,
    // 未指定就不要放進 config，flatpickr 的 getDefaultHours 直接讀這兩個值，塞 undefined 會算出 NaN
    ...(defaultHour !== undefined ? { defaultHour } : {}),
    ...(defaultMinute !== undefined ? { defaultMinute } : {}),
    defaultDate: initialValueRef.current,
  }), [locale, enableTime, minDate, maxDate, defaultHour, defaultMinute]);

  // onChange 收進 ref：呼叫端常直接寫 inline arrow（如 SignUpForm、AccountSettings），若讓它進到
  // handleChange 的依賴，每次 render 都會換新參照，穿透下面 FlatpickrInstance 的 memo，
  // 導致 <Flatpickr> 重繪 → 套件內部把 flatpickr 實例整個 destroy 後重建。實際症狀：SignUpForm 的
  // 重寄驗證碼倒數每秒 setState 一次，使用者在倒數期間開著生日日曆，日曆會每秒被關掉一次
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // 必須交出副本：flatpickr 的 hook 拿到的是實例內部的 selectedDates 本體（index.js:1836），
  // 而調整時分走的 setHours 是就地 mutate 同一個 Date 物件（index.js:221-223）。若把它直接交給
  // 父層 state，下次 setState 拿到的是同一個參照，React 會 bail out 不重繪，依賴這個值的驗證
  // （例如排程的「必須是未來時間」）就會停在舊結果
  const handleChange = useCallback(
    ([date]) => onChangeRef.current(date ? new Date(date.getTime()) : null),
    []
  );

  // ESC 只該關日曆。flatpickr 對 ESC 只有 preventDefault、沒有 stopPropagation（index.js:1062-1065），
  // 事件會一路冒泡到 document，被 Modal 綁在那裡的 keydown 收走，連整個 modal 一起關掉（在
  // PostEditModal 裡就是編輯到一半的內容整包消失）。改成自己在捕獲階段處理掉：攔下事件並比照
  // flatpickr 的 focusAndClose（index.js:1522-1530）把焦點還給 input 再關日曆。
  // 日曆沒開時直接放行，維持「ESC 關 modal」的原本行為。
  // 注意：下面併進 inputProps 時會蓋掉呼叫端自帶的 onKeyDownCapture，目前無人這樣用，日後有需要再改串接
  const handleEscapeCapture = useCallback((e) => {
    const instance = instanceRef.current;
    if (e.key !== 'Escape' || !instance?.isOpen) return;
    e.stopPropagation();
    instance.input.focus();
    instance.close();
  }, []);

  // 必須穩定（空依賴）：這個 callback 本身也是 <Flatpickr> 的一個 prop，
  // 若每次 render 都換新參照，會跟 value 直接受控時一樣觸發套件內部重建
  const handleCreate = useCallback((instance) => {
    instanceRef.current = instance;
    // 日曆掛在 document.body，不在 React 樹裡，焦點在日曆內按下的 ESC 不會經過上面那個
    // onKeyDownCapture，得直接綁在日曆容器上（捕獲階段，搶在套件自己的 keydown 之前）。
    // 行動裝置走原生 picker，flatpickr 會跳過 build()（index.js:74-75），沒有日曆容器可綁
    if (!instance.isMobile) {
      instance.calendarContainer.addEventListener('keydown', handleEscapeCapture, true);
    }
  }, [handleEscapeCapture]);

  // value 變化（不論是外部主動指定，或使用者選完值後父層 state 回灌）一律走 setDate 命令式同步。
  // 但值其實沒變（使用者互動 → onChange → 父層 state → 同一個時間回灌）時要跳過：setDate() 會重寫
  // 時分欄的 value 並重畫日曆，撞上使用者正在編輯的欄位就會把輸入到一半的內容打掉
  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance) return;
    const current = instance.selectedDates[0];
    if (value instanceof Date && current && current.getTime() === value.getTime()) return;
    instance.setDate(value ?? null, false);
  }, [value]);

  return (
    <FlatpickrInstance
      options={options}
      onChange={handleChange}
      onCreate={handleCreate}
      inputProps={{ ...inputProps, onKeyDownCapture: handleEscapeCapture }}
      className={className}
    />
  );
}

export default DatePicker;
